import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { AppPages, PortalConfigurationType, VendorContactRole } from '@trnsact/trnsact-shared-types';
import { portalConfigurationsSelectors } from 'redux/portalConfigurationReducer';
import { usePortalConfiguration } from 'modules/portalConfigurations/api';
import { usePortalConfigurationChecks } from 'modules/portalConfigurations';
import { PortalConfigurationRolesSettingsKeys } from 'types';
import { RoutingAccessPortalConfiguration } from '../types';

export const useUserAccessTable = () => {
  const rawConfig = useSelector((state: any) =>
    portalConfigurationsSelectors.configDataByType(state, PortalConfigurationType.RoutingAccess)
  );

  const [localConfig, setLocalConfig] = useState<RoutingAccessPortalConfiguration | undefined>(
    rawConfig?.jsonDefinition?.config
  );

  const { handleSaveConfiguration, isPortalConfigurationSaving } = usePortalConfiguration();
  const { checkPortalConfigEntryForRole, hasPortalConfiguration } = usePortalConfigurationChecks();

  useEffect(() => {
    const config: RoutingAccessPortalConfiguration | undefined = rawConfig?.jsonDefinition?.config;
    if (config) setLocalConfig(config);
  }, [rawConfig]);

  const isDisabledByPcw = useCallback(
    (page: AppPages, role: VendorContactRole): boolean => {
      switch (page) {
        case AppPages.UserManagement:
          return checkPortalConfigEntryForRole(
            PortalConfigurationType.UserManagement,
            PortalConfigurationRolesSettingsKeys.Hidden,
            role
          );
        case AppPages.PortalSetup:
          return checkPortalConfigEntryForRole(
            PortalConfigurationType.PortalSetup,
            PortalConfigurationRolesSettingsKeys.Hidden,
            role
          );
        case AppPages.AftermarketProducts:
          if (!hasPortalConfiguration(PortalConfigurationType.Aftermarket)) return false;

          return !(
            checkPortalConfigEntryForRole(
              PortalConfigurationType.Aftermarket,
              PortalConfigurationRolesSettingsKeys.AssignedOnly,
              role
            ) ||
            checkPortalConfigEntryForRole(
              PortalConfigurationType.Aftermarket,
              PortalConfigurationRolesSettingsKeys.ReadOnly,
              role
            )
          );
        case AppPages.Inventory:
          if (!hasPortalConfiguration(PortalConfigurationType.Inventory)) return false;

          return !(
            checkPortalConfigEntryForRole(
              PortalConfigurationType.Inventory,
              PortalConfigurationRolesSettingsKeys.AssignedOnly,
              role
            ) ||
            checkPortalConfigEntryForRole(
              PortalConfigurationType.Inventory,
              PortalConfigurationRolesSettingsKeys.ReadOnly,
              role
            )
          );
        default:
          return false;
      }
    },
    [checkPortalConfigEntryForRole, hasPortalConfiguration]
  );

  const isChecked = (page: AppPages, role: VendorContactRole): boolean => {
    if (isDisabledByPcw(page, role)) return false;
    return !(localConfig?.accessByPage[page]?.rolesSettings?.hidden ?? []).includes(role);
  };

  const handleChange = useCallback((page: AppPages, role: VendorContactRole, checked: boolean) => {
    setLocalConfig(prev => {
      if (!prev) return prev;
      const currentHidden = prev.accessByPage[page]?.rolesSettings?.hidden ?? [];
      const newHidden = checked ? currentHidden.filter(r => r !== role) : [...currentHidden, role];
      return {
        ...prev,
        accessByPage: {
          ...prev.accessByPage,
          [page]: { rolesSettings: { hidden: newHidden } },
        },
      };
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!localConfig) return;
    await handleSaveConfiguration({
      ...rawConfig,
      jsonDefinition: { config: localConfig },
    });
  }, [localConfig, rawConfig, handleSaveConfiguration]);

  return {
    isLoading: !localConfig,
    isSaving: isPortalConfigurationSaving,
    isChecked,
    isDisabledByPcw,
    handleChange,
    handleSave,
  };
};
